package co.maytes.standupbot.dashboard.features.home

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.styles.HomeArt
import co.maytes.standupbot.dashboard.styles.HomeClasses
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Tracking
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.CSSNumeric
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div

@Composable
fun HmDiv(vararg classes: String, modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Div(attrs = modifier.classNames(*classes).toAttrs()) { content() }
}

@Composable
fun HmLink(path: String, vararg classes: String, modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    val ctx = rememberPageContext()
    A(
        href = path,
        attrs = modifier
            .classNames(*classes)
            .onClick { event ->
                if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                    event.preventDefault()
                    ctx.router.navigateTo(path)
                }
            }
            .toAttrs(),
    ) { content() }
}

@Composable
fun rememberNavigator(): (String) -> Unit {
    val ctx = rememberPageContext()
    return { path -> ctx.router.navigateTo(path) }
}

fun Modifier.tinted(color: String): Modifier = styleModifier { property(HomeArt.TINT_VAR, color) }

fun Modifier.staggerIn(index: Int): Modifier {
    val delay = (index * Motion.STAGGER_STEP_MS).coerceAtMost(Motion.STAGGER_CAP_MS)
    return classNames(Motion.ENTER_CLASS).styleModifier {
        if (delay > 0) property("animation-delay", "${delay}ms")
    }
}

fun Modifier.stack(gap: CSSNumeric = Spacing.sm): Modifier = styleModifier {
    property("display", "flex")
    property("flex-direction", "column")
    property("gap", gap.toString())
    property("min-width", "0")
}

fun Modifier.rowWrap(): Modifier = styleModifier {
    property("display", "flex")
    property("align-items", "center")
    property("flex-wrap", "wrap")
    property("gap", Spacing.xs.toString())
    property("min-width", "0")
}

fun Modifier.rowBetween(): Modifier = styleModifier {
    property("display", "flex")
    property("align-items", "center")
    property("justify-content", "space-between")
    property("gap", Spacing.sm.toString())
}

fun Modifier.fill(): Modifier = styleModifier {
    property("flex", "1 1 0")
    property("min-width", "0")
}

fun classesOf(vararg names: String?): Array<String> = names.filterNotNull().toTypedArray()

@Composable
fun HmTitle(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.h4.size)
            .fontWeight(Typography.h4.weight)
            .color(Palette.textPrimary)
            .styleModifier { property("letter-spacing", Tracking.tight.toString()) },
    )
}

@Composable
fun HmMuted(text: String, modifier: Modifier = Modifier) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .color(Palette.textSecondary)
            .then(modifier),
    )
}

@Composable
fun HmLead(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.body.size)
            .color(Palette.textSecondary)
            .styleModifier { property("line-height", Typography.body.lineHeight.toString()) },
    )
}

@Composable
fun HmStrong(text: String, modifier: Modifier = Modifier) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .fontWeight(Typography.label.weight)
            .color(Palette.textPrimary)
            .then(modifier),
    )
}

@Composable
fun HmIconTile(tint: String, icon: @Composable () -> Unit) {
    HmDiv(HomeClasses.ICON_TILE, modifier = Modifier.tinted(tint)) { icon() }
}

@Composable
fun HmCardHead(
    title: String,
    tint: String,
    icon: @Composable () -> Unit,
    trailing: (@Composable () -> Unit)? = null,
) {
    HmDiv(HomeClasses.CARD_HEAD) {
        HmDiv(modifier = Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("gap", Spacing.sm.toString())
            property("min-width", "0")
        }) {
            HmIconTile(tint, icon)
            HmTitle(title)
        }
        trailing?.invoke()
    }
}

@Composable
fun HmBone(width: String, height: String) {
    HmDiv(HomeClasses.BONE, modifier = Modifier.styleModifier {
        property("width", width)
        property("height", height)
        property("max-width", "100%")
    }) {}
}

@Composable
fun HmGhost(widthPercent: Int, height: String, accent: Boolean = false) {
    HmDiv(
        *classesOf(HomeClasses.GHOST, if (accent) HomeClasses.GHOST_ACCENT else null),
        modifier = Modifier.styleModifier {
            property("width", "$widthPercent%")
            property("height", height)
        },
    ) {}
}

@Composable
fun HmEmpty(
    title: String,
    subtitle: String,
    actionLabel: String?,
    onAction: () -> Unit,
    art: @Composable () -> Unit,
) {
    HmDiv(HomeClasses.EMPTY) {
        HmDiv(HomeClasses.EMPTY_ART) { art() }
        HmTitle(title)
        HmMuted(subtitle)
        if (actionLabel != null) {
            Button(label = actionLabel, variant = ButtonVariant.Secondary, size = ButtonSize.Sm, onClick = onAction)
        }
    }
}
